import { useQuery } from '@tanstack/react-query';

import { CACHE } from '../app/query-config';
import type { DocumentEditLogEntryDto } from '../services';
import { formatDateTime } from '../ui/format';
import { Skeleton } from '../ui/primitives';

/**
 * The edit trail — COLLAPSED, and deliberately not a version picker.
 *
 * There is exactly ONE voucher and ONE invoice per booking, and one download of
 * each which always renders the current state. This list is an AUDIT record: who
 * touched the document, when, and which FIELD NAMES changed. There is no payload
 * here to restore and nothing to download, because presenting edits as "v1 / v2 /
 * v3" is precisely the design this flow rejects — it invites someone to send a
 * customer a superseded document.
 *
 * Collapsed by default and mounted only when opened, so the day-to-day flow does
 * not carry an audit log it never reads, and does not pay for the request either.
 */
export function DocumentEditHistory({
  fetchEdits,
  queryKey,
}: {
  fetchEdits: (signal?: AbortSignal) => Promise<readonly DocumentEditLogEntryDto[]>;
  queryKey: readonly unknown[];
}): JSX.Element {
  return (
    <details className="group border-t border-neutral-200 px-5 py-3">
      <summary className="cursor-pointer list-none text-[11.5px] font-semibold leading-none text-neutral-600 hover:text-ink">
        <span className="inline-block w-3 transition-transform group-open:rotate-90">›</span> Edit
        history
      </summary>
      <EditList fetchEdits={fetchEdits} queryKey={queryKey} />
    </details>
  );
}

/**
 * Split out so the query only runs when the `<details>` is open.
 *
 * React would otherwise mount the hook regardless of the disclosure state and
 * fetch an audit log for every document on the screen.
 */
function EditList({
  fetchEdits,
  queryKey,
}: {
  fetchEdits: (signal?: AbortSignal) => Promise<readonly DocumentEditLogEntryDto[]>;
  queryKey: readonly unknown[];
}): JSX.Element {
  const { data: edits, isPending } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchEdits(signal),
    // An audit trail only grows, and only when someone on this screen edits the
    // document — at which point the editor invalidates it explicitly.
    ...CACHE.record,
  });

  /*
   * A skeleton shaped like the list, not the word "Loading…".
   *
   * The text version collapsed to one short line and then the real list pushed
   * everything below it down the page — the spinner-then-jump the rest of this
   * screen already avoids. Two rows because that is the common length of an
   * edit log, so the reflow when it arrives is small in both directions.
   */
  if (isPending) {
    return (
      <div className="mt-3 space-y-2" aria-hidden="true">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-3/5" />
      </div>
    );
  }

  if (edits === undefined || edits.length === 0) {
    return (
      <p className="mt-3 text-[11.5px] leading-[1.5] text-neutral-600">
        This document has not been edited since it was raised.
      </p>
    );
  }

  return (
    <ol className="mt-3 space-y-2">
      {edits.map((edit) => (
        <li key={edit.id} className="text-[11.5px] leading-[1.45] text-neutral-600">
          <time dateTime={edit.editedAt} className="font-semibold text-ink">
            {formatDateTime(edit.editedAt)}
          </time>{' '}
          — changed {edit.changesSummary}
        </li>
      ))}
    </ol>
  );
}
